import React, { useCallback, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Workspace } from "../../types";
import useUserProfile from "../hooks/profile/useUserProfile";
import useOutsideClick from "../hooks/useOutsideClick";
import useSelectWorkspace from "../hooks/workspace/useSelectWorkspace";
import useUserWorkspaces from "../hooks/workspace/useUserWorkspaces";
import useWorkspace from "../hooks/workspace/useWorkspace";
import IconFont from "../elements/IconFont";
import { TriggerToast } from "./Toaster";
import { appRoutes } from "../../routes/appRoutes";
import {
  filterWorkspaces,
  getWorkspaceDropdownHeightClass,
  getHighlightedWorkspaceIndex,
  getNextWorkspaceIndex,
  shouldShowWorkspaceSearch,
} from "./workspaceSwitcherSearch";
import ReactTooltip from "./ReactTooltip";

interface WorkspaceSwitcherProps {
  onChange?: (workspace: Workspace) => void | null;
  isDesktopCollapsed?: boolean;
}

const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  onChange,
  isDesktopCollapsed = false,
}: WorkspaceSwitcherProps): React.ReactElement => {
  const { workspace, isLoading: isLoadingWorkspace } = useWorkspace();
  const { profile, isLoading: isLoadingProfile } = useUserProfile();

  const selectWorkspace = useSelectWorkspace();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] =
    React.useState<boolean>(false);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [keyboardActiveIndex, setKeyboardActiveIndex] =
    React.useState<number>(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] =
    React.useState<boolean>(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const workspaceButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const closeWorkspaceSwitcher = useCallback(() => {
    setIsOpen(false);
    setSearchValue("");
    setKeyboardActiveIndex(0);
    setIsKeyboardNavigating(false);
  }, []);

  const ref = useOutsideClick(
    closeWorkspaceSwitcher,
  ) as React.RefObject<HTMLDetailsElement>;

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (isSwitchingWorkspace) return;
    if (isOpen) {
      closeWorkspaceSwitcher();
      return;
    }
    setKeyboardActiveIndex(0);
    setIsKeyboardNavigating(false);
    setIsOpen(true);
  };

  const handleCreateWorkspaceClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (profile?.can_create_workspace === false) {
      event.preventDefault();
      TriggerToast("Access Denied: Join Waitlist", undefined, "error");
      return;
    }
    navigate(appRoutes.workspace.create);
  };

  const handleSelectWorkspace = useCallback(
    async (ws: Workspace) => {
      if (isSwitchingWorkspace) return;

      closeWorkspaceSwitcher();
      if (onChange) {
        onChange(ws);
      }

      if (ws.id === workspace?.id) return;

      setIsSwitchingWorkspace(true);
      try {
        await selectWorkspace({ workspaceId: ws.id, workspace: ws });
      } finally {
        setIsSwitchingWorkspace(false);
      }
    },
    [
      closeWorkspaceSwitcher,
      isSwitchingWorkspace,
      onChange,
      selectWorkspace,
      workspace?.id,
    ],
  );

  const orderedWorkspaces = useMemo(() => {
    const workspaces = profile?.workspaces ?? [];
    if (!workspaces.length) return workspaces;

    const selected = workspaces.find((ws) => ws.id === workspace?.id);
    const remaining = workspaces
      .filter((ws) => ws.id !== workspace?.id)
      .slice()
      .sort((a, b) => {
        const aTime = a.created_at ? Date.parse(a.created_at) : 0;
        const bTime = b.created_at ? Date.parse(b.created_at) : 0;
        return bTime - aTime;
      });

    return selected ? [selected, ...remaining] : remaining;
  }, [profile?.workspaces, workspace?.id]);

  const showWorkspaceSearch = shouldShowWorkspaceSearch(
    orderedWorkspaces.length,
  );
  const workspaceIds = useMemo(
    () => orderedWorkspaces.map(({ id }) => id),
    [orderedWorkspaces],
  );
  const { userWorkspaces } = useUserWorkspaces({
    enabled: showWorkspaceSearch,
    workspaceIds,
  });
  const filteredWorkspaces = useMemo(
    () =>
      filterWorkspaces(
        orderedWorkspaces,
        userWorkspaces,
        showWorkspaceSearch ? searchValue : "",
      ),
    [orderedWorkspaces, searchValue, showWorkspaceSearch, userWorkspaces],
  );
  const dropdownHeightClass = getWorkspaceDropdownHeightClass(
    filteredWorkspaces.length,
    showWorkspaceSearch,
  );
  const selectedWorkspaceIndex = filteredWorkspaces.findIndex(
    ({ id }) => id === workspace?.id,
  );
  const highlightedWorkspaceIndex = getHighlightedWorkspaceIndex(
    keyboardActiveIndex,
    isKeyboardNavigating,
    selectedWorkspaceIndex,
    filteredWorkspaces.length,
  );
  const highlightedWorkspace = filteredWorkspaces[highlightedWorkspaceIndex];

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.nativeEvent.isComposing || isSwitchingWorkspace) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setKeyboardActiveIndex(
        getNextWorkspaceIndex(
          highlightedWorkspaceIndex,
          direction,
          filteredWorkspaces.length,
        ),
      );
      setIsKeyboardNavigating(true);
      return;
    }

    if (event.key === "Enter") {
      const workspaceToSelect = highlightedWorkspace ?? filteredWorkspaces[0];
      if (!workspaceToSelect) return;
      event.preventDefault();
      void handleSelectWorkspace(workspaceToSelect);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeWorkspaceSwitcher();
    }
  };

  React.useEffect(() => {
    if (!isOpen || !showWorkspaceSearch) return;
    const frameId = window.requestAnimationFrame(() =>
      searchInputRef.current?.focus(),
    );
    return () => window.cancelAnimationFrame(frameId);
  }, [isOpen, showWorkspaceSearch]);

  React.useEffect(() => {
    setKeyboardActiveIndex((currentIndex) => {
      if (!filteredWorkspaces.length) return -1;
      if (currentIndex < 0 || currentIndex >= filteredWorkspaces.length)
        return 0;
      return currentIndex;
    });
  }, [filteredWorkspaces]);

  React.useEffect(() => {
    if (!isOpen || highlightedWorkspaceIndex < 0) return;
    workspaceButtonRefs.current[highlightedWorkspaceIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [highlightedWorkspaceIndex, isOpen]);

  const currentWorkspaceName = useMemo(() => {
    if (workspace?.name) return workspace.name;
    if (profile?.active_workspace?.[0]?.name)
      return profile.active_workspace[0].name;
    if (orderedWorkspaces?.[0]?.name) return orderedWorkspaces[0].name;
    return "Select workspace";
  }, [workspace?.name, profile?.active_workspace, orderedWorkspaces]);

  return (
    <>
      <details
        ref={ref}
        open={isOpen}
        aria-busy={isSwitchingWorkspace}
        className="ui-workspace-switcher block relative w-full select-none"
      >
        <summary
          id="workspace-switcher-trigger"
          onClick={handleOnClick}
          title={isDesktopCollapsed ? undefined : currentWorkspaceName}
          aria-label={`Current workspace: ${currentWorkspaceName}`}
          className={`ui-workspace-switcher-trigger relative flex items-center gap-[4px] rounded-[6px] border border-border-input bg-background-input p-[8px] ${isSwitchingWorkspace ? "cursor-wait opacity-80" : ""}`}
        >
          <IconFont
            name="layers-2"
            size={16}
            className="ui-workspace-switcher-icon shrink-0"
          />
          <span className="ui-workspace-switcher-name flex gap-[6px] items-center text-body text-md font-normal leading-6 tracking-normal truncate">
            {isLoadingWorkspace ? "Loading..." : currentWorkspaceName}
          </span>
          <span className="ui-workspace-switcher-chevron flex items-center justify-center shrink-0">
            <IconFont name="chevron-down" size={16} />
          </span>
        </summary>
        <div
          className={`ui-workspace-switcher-popover ui-layer-popover absolute flex w-[280px] max-h-[calc(100vh-180px)] flex-col gap-[8px] overflow-hidden rounded-[8px] border border-border-default bg-background-surface-01 p-[16px] shadow-[0px_1px_4px_0px_var(--ui-shadow-sm)] ${dropdownHeightClass}`}
        >
          <div className="flex items-center justify-between w-full">
            <h2 className="text-md font-semibold leading-6 tracking-normal text-heading">
              Workspace
            </h2>
            <NavLink
              to={appRoutes.workspace.create}
              onClick={handleCreateWorkspaceClick}
              className="flex items-center justify-center"
            >
              <IconFont name="plus" size={24} className="text-button-link" />
            </NavLink>
          </div>
          <div className="mt-[8px] flex min-h-0 w-full flex-1 flex-col gap-[8px]">
            {showWorkspaceSearch && (
              <div className="relative w-full shrink-0">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                    setKeyboardActiveIndex(0);
                    setIsKeyboardNavigating(false);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search workspaces"
                  aria-label="Search workspaces"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="workspace-switcher-results"
                  aria-expanded={isOpen}
                  aria-activedescendant={
                    highlightedWorkspace
                      ? `workspace-option-${highlightedWorkspace.id}`
                      : undefined
                  }
                  autoComplete="off"
                  className="control-base h-[36px] pr-[36px]"
                />
                {searchValue && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchValue("");
                      setKeyboardActiveIndex(0);
                      setIsKeyboardNavigating(false);
                      searchInputRef.current?.focus();
                    }}
                    aria-label="Clear workspace search"
                    className="absolute right-[6px] top-1/2 flex h-[24px] w-[24px] -translate-y-1/2 items-center justify-center text-body-muted hover:text-heading"
                  >
                    <IconFont name="x" size={16} className="text-inherit" />
                  </button>
                )}
              </div>
            )}
            <div
              id="workspace-switcher-results"
              role={showWorkspaceSearch ? "listbox" : undefined}
              className="min-h-0 w-full flex-1 scrollable"
            >
              {isLoadingProfile ? "Loading..." : null}
              {profile && filteredWorkspaces.length > 0 ? (
                <div className="flex w-full flex-col gap-[8px]">
                  {filteredWorkspaces.map((ws, index) => (
                    <div key={ws.id}>
                      <button
                        ref={(element) => {
                          workspaceButtonRefs.current[index] = element;
                        }}
                        id={`workspace-option-${ws.id}`}
                        type="button"
                        role={showWorkspaceSearch ? "option" : undefined}
                        aria-selected={
                          showWorkspaceSearch
                            ? ws.id === workspace?.id
                            : undefined
                        }
                        tabIndex={showWorkspaceSearch ? -1 : 0}
                        disabled={isSwitchingWorkspace}
                        onClick={() => handleSelectWorkspace(ws)}
                        data-workspace-id={ws.id}
                        className={`ui-workspace-item ${isSwitchingWorkspace ? "cursor-wait opacity-70" : ""} ${(showWorkspaceSearch ? index === highlightedWorkspaceIndex : ws.id === workspace?.id) ? "ui-workspace-item-active" : "ui-workspace-item-idle"}`}
                      >
                        <IconFont
                          name="layers-2"
                          size={16}
                          className="shrink-0 text-inherit"
                        />
                        <span className="js-ui-workspace-name truncate text-ellipsis">
                          {ws.name}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              {!isLoadingProfile &&
              profile &&
              filteredWorkspaces.length === 0 ? (
                <p
                  role="status"
                  className="px-[8px] py-[12px] text-center text-sm text-body-muted"
                >
                  No workspaces found.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </details>
      {isDesktopCollapsed && !isOpen && (
        <ReactTooltip anchorSelect="#workspace-switcher-trigger" place="right">
          Workspaces
        </ReactTooltip>
      )}
    </>
  );
};

export default WorkspaceSwitcher;
